<template>
  <side-panel-inner headerTitle="Submit an idea" class="p-form-create" :on-close="`/collaboration/question/${idea.question_id}`">
    <template slot="actions">
      <base-button
        @click="submitIdea"
        :primary="true"
        iconBefore="pencil"
        v-if="!questionClosed"
      >Publish</base-button>
    </template>

    <div v-if="editMode && idea.user.id !== $store.state.auth.currentUser.id">
      <h1>Permission denied</h1>
      <p>Sorry, you do not have permission to edit this idea.</p>
    </div>

    <div v-else-if="questionClosed">
      <h1>Question Closed</h1>
      <p>Sorry, the question has been marked as closed, and so ideas can not be created or updated.</p>
    </div>

    <!-- Policy Title -->
    <form v-else-if="!loading" action>
      <message type="warning" v-if="idea.status === 'review'">
        <h3>Please review your idea</h3>
        <p>The question owner has asked you to review your idea. The feedback given is:</p>
        <p>{{idea.review_reason}}</p>
      </message>
      <message type="error" v-if="showValidationMessage">
        <h3>Form Invalid</h3>
        <p>Form is invalid, please check and try again.</p>
        <ul>
          <li v-for="error in errors.items" :key="error.id">{{error.msg}}</li>
        </ul>
      </message>

      <form-field
        full-width
        required
        label="Idea Title"
        name="idea_title"
        :max-length="45"
        show-counter
        helper-text="Get your idea noticed, keep it simple"
        v-validate="'required'"
        data-vv-as="question title"
        :error="errors.first('idea_title')"
        v-model="idea.title"
      ></form-field>

      <form-field
        label="Additional Information"
        name="description"
        helper-text="Give more information about your idea and why people should vote for it"
        :required="true"
      >
        <quill-editor
          :options="quillEditorOptions"
          v-model="idea.description"
          v-validate="'required'"
          data-vv-as="description"
          name="description"
        ></quill-editor>
        <p
          class="c-form__validation-message"
          v-if="errors.first('description')"
        >{{errors.first('description')}}</p>
      </form-field>

      <p>
        <base-button
          type="button"
          :asLink="true"
          iconBefore="plus-circle"
          @click="toggleAttachments"
        >Add attachments</base-button>
      </p>

      <file-upload
        name="attachment"
        :fileId="null"
        :bypassDuplicate="true"
        :fileTypes="['png','pdf','doc','docx','xls','xlsx','gif','jpg']"
        v-if="addFile"
        @uploaded="onFileUploaded"
        :showExtensions="false"
        :multiple="true"
      ></file-upload>
    </form>
  </side-panel-inner>
</template>

<script>
import { quillEditor } from "vue-quill-editor";
// import 'quill/dist/quill.core.css'
import "quill/dist/quill.snow.css";
// import 'quill/dist/quill.bubble.css'
import FileUpload from "@/components/FileUpload";
import { mapState } from "vuex";

export default {
  name: "question-create",

  data() {
    return {
      showValidationMessage: false,
      loading: false,
      resourceCreated: false,
      responseData: {
        resourceId: null
      },
      addFile: false,
      editMode: false,
      quillEditorOptions: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"]
          ],
          clipboard: {
            matchVisual: false
          }
        },

        formats: ["bold", "italic", "underline", "header", "list", "link"]
      }
    };
  },

  methods: {
    followQuestion() {
      // Once submitted, we need to get the user to auto-follow the question
      this.$store
        .dispatch("Questions/follow", this.question.id)
        .then(resp => {
          this.$store.dispatch(
            "notifications/create",
            {
              body: `You are now following this question`,
              type: "success"
            },
            { root: true }
          );
          this.question.user_favourite = true;
        })
        .catch(err => {
          this.$store.dispatch(
            "notifications/create",
            {
              body: `There was a problem - ${err}`,
              type: "error"
            },
            { root: true }
          );
        });
    },
    submitIdea() {
      const vm = this;
      this.$validator.validate().then(result => {
        if (!result) {
          vm.showValidationMessage = true;
        } else {
          let endpoint = this.editMode ? "Ideas/update" : "Ideas/create";
          // if we are in review, change the endpoint
          if (vm.idea.status === "review") {
            endpoint = "Ideas/resubmit";
          }
          vm.$store
            .dispatch(endpoint, this.idea)
            .then(resp => {
              this.$store.dispatch("CLOSE_PANEL");
              // Idea created, go back to the question page
              vm.$store.dispatch(
                "notifications/create",
                {
                  body: `Your idea was successfully posted`,
                  type: "success"
                },
                { root: true }
              );
              // TODO: We should now really move to the question, but we need to get the response from the API first
              this.followQuestion();
            })
            .catch(error => {
              vm.$store.dispatch(
                "notifications/create",
                {
                  title: "Error: ",
                  body: `Your idea was not posted - ${
                    error.response.data.error.message
                  }`,
                  type: "danger"
                },
                { root: true }
              );
            });
        }
      });
    },
    toggleAttachments() {
      this.addFile = !this.addFile;
    },
    onFileUploaded(val) {
      this.idea.file_ids.push(val.id);
    }
  },

  computed: {
    ...mapState("Ideas", ["idea"]),
    ...mapState("Questions", ["question"]),
    questionClosed() {
      if (
        this.$dayjs(this.question.end_date).valueOf() < this.$dayjs().valueOf()
      ) {
        return true;
      }
      return this.question.status === "closed";
    }
  },

  components: {
    FileUpload,
    "quill-editor": quillEditor
  },

  beforeCreate() {
    // if we are disabled, go back
    if (this.$route.params.ideaId) {
      this.$store.dispatch("Ideas/read", { ...this.$route.params });
    } else {
      this.$store.dispatch("Ideas/resetState");
    }
  },

  created() {
    if (this.$route.params.ideaId) {
      this.editMode = true;
    }
  }
};
</script>


<style lang="scss" scoped>
.p-form-create {
  background-color: tint($colour-nhs-orange, 6);
  @media screen and (min-width: 1024px) {
    background-image: url(/assets/idea.svg);
    background-position: 0 100px;
    background-attachment: scroll;
  }

  form {
    @media screen and (min-width: 1024px) {
      margin-left: 70px;
    }
  }
}
</style>

<style lang="scss">
.ql-toolbar {
  background: $colour-nhs-pale-grey;
}
.ql-container {
  background: $colour-nhs-core-white;
  font-size: 16px;

  @media (min-width: 768px) {
    font-size: 13px;
  }
}
</style>
